<template>
  <UIDropdown
    class="UIMenuDropdown"
    :items="rows"
    :isDisabled="isDisabled"
    :placement="placement"
    @clickedOption="onClickedOption"
  >
    <!-- The trigger is a caller-supplied command button (⋮), not a select field, so
         ui-lib's toggle chrome is replaced wholesale; the anchor still owns opening. -->
    <template #toggle-container="{ isOpen }">
      <slot name="toggle" :isOpen="isOpen" />
    </template>

    <!-- ui-lib's list item owns click / keyboard / close and skips disabled rows, so
         the row only carries content. It types the payload as a bare dropdown item,
         hence `rowOf` to narrow it back to the menu row we passed in. -->
    <template #option="{ option }">
      <!-- A row with a `tooltip` is wrapped in UITooltip (whose default slot is the
           trigger); otherwise the same content renders bare through RowFragment. Both
           take it as their default slot, so the icon + label / #item slot is authored
           exactly once. -->
      <component
        :is="rowOf(option).tooltip ? UITooltip : RowFragment"
        v-bind="wrapperProps(rowOf(option))"
      >
        <span
          class="UIMenuDropdown__item"
          :data-variant="rowOf(option).variant"
          :data-group-start="!!rowOf(option).isGroupStart"
        >
          <!-- Default item: icon + label. Override with #item for bespoke rows. -->
          <slot name="item" :option="rowOf(option)">
            <!-- A row still resolving what it needs shows a spinner in the icon's
                 place, so the wait reads as "not ready yet" rather than as a
                 permanently dead row. Boxed to the icon's size so the label holds
                 still when the spinner is swapped back out. -->
            <span
              v-if="rowOf(option).isLoading"
              class="UIMenuDropdown__spinner"
            >
              <UISpinner size="sm" />
            </span>
            <UIIcon
              v-else-if="rowOf(option).icon"
              class="UIMenuDropdown__icon"
              :name="rowOf(option).icon!"
              aria-hidden="true"
            />
            <span class="UIMenuDropdown__label">
              {{ rowOf(option).label }}
            </span>
          </slot>
        </span>

        <template v-if="rowOf(option).tooltip" #content>
          {{ rowOf(option).tooltip }}
        </template>
      </component>
    </template>
  </UIDropdown>
</template>

<script lang="ts" setup>
import { computed, type FunctionalComponent } from 'vue'

import { UIDropdown, UIIcon, UISpinner, type IIcons } from '@foundever/ui-lib'

import UITooltip from '../tooltips/UITooltip.vue'

import type { Placement } from '@floating-ui/vue'

/**
 * One row in a {@link UIMenuDropdown}. A `divider` row renders a separator instead
 * of an action; `variant: 'destructive'` renders the row in the error color. Pick a
 * chosen row from the `select` event (dispatch on `value`).
 */
export interface IMenuDropdownItem {
  label: string
  value: string
  icon?: IIcons
  variant?: 'destructive'
  divider?: boolean
  isDisabled?: boolean
  /**
   * The row is waiting on something before it can be used (e.g. a check of what it
   * would act on). Renders a spinner in place of its icon and forces the row
   * disabled, so a click can't land on an answer that isn't in yet.
   */
  isLoading?: boolean
  /** Hover hint for the row — typically the reason a disabled row can't be used. */
  tooltip?: string
  /** Set by this wrapper (not by callers): first row of a group, ruled off above. */
  isGroupStart?: boolean
}

const props = withDefaults(
  defineProps<{
    items: IMenuDropdownItem[]
    isDisabled?: boolean
    placement?: Placement
  }>(),
  {
    isDisabled: false,
    placement: 'bottom-end',
  },
)

const emit = defineEmits<{
  select: [item: IMenuDropdownItem]
}>()

// Wrapper-less stand-in used when a row has no tooltip: renders its default slot directly
// (no extra element), so the row's icon + label markup is authored once whether or not a
// UITooltip wraps it. (A tooltip must wrap the content — it needs a real box to anchor
// to — so the wrapper can't be hoisted out of the branch.)
const RowFragment: FunctionalComponent = (_, { slots }) =>
  slots.default?.() ?? null

function isDivider(item: IMenuDropdownItem) {
  return item.divider === true || item.value === 'divider'
}

/**
 * ui-lib renders every `items` entry as a selectable row — it has no separator row —
 * so dividers are dropped here and the row that followed one is marked as the start of
 * a group, which the style block below rules off.
 */
const rows = computed<IMenuDropdownItem[]>(() => {
  const menuRows: IMenuDropdownItem[] = []
  let startsGroup = false

  for (const item of props.items) {
    if (isDivider(item)) {
      // A leading divider has nothing to separate from.
      startsGroup = menuRows.length > 0
      continue
    }
    menuRows.push({
      ...item,
      // `isLoading` implies disabled — ui-lib skips disabled rows, and that skip is
      // what actually stops the click; the spinner alone is only a hint.
      isDisabled: item.isDisabled === true || item.isLoading === true,
      ...(startsGroup ? { isGroupStart: true } : {}),
    })
    startsGroup = false
  }

  return menuRows
})

/**
 * ui-lib hands the slot and the `clickedOption` emit the very `rows` entry it is
 * rendering (`renderSlot($slots, 'option', { option: item })`), but types both as a
 * bare `{ label, value }`. So this is a cast, not a lookup — narrowing the payload
 * back to the row we passed in.
 */
function rowOf(option: { value: string }): IMenuDropdownItem {
  return option as IMenuDropdownItem
}

// Wrapper props only apply to the tooltip branch; a bare RowFragment must receive none, or
// Vue warns about extraneous fallthrough attributes. UITooltip passes attrs down to Tippy,
// so the class lands on the trigger element wrapping the row content.
function wrapperProps(item: IMenuDropdownItem) {
  return item.tooltip ? { class: 'UIMenuDropdown__row', placement: 'left' } : {}
}

function onClickedOption(option: { label: string; value: string }) {
  emit('select', rowOf(option))
}
</script>

<!--
  UIDropdown teleports its menu to <body> with no per-instance content class, so its
  content lives outside this component's root — scoped `data-v` styling can't reach it.
  Style it globally, keyed on the (globally unique) `UIMenuDropdown__*` row classes.
  This block is the single source of truth for divider / destructive / icon styling
  across every menu that uses this wrapper.
-->
<style lang="scss">
.UIMenuDropdown__item {
  display: flex;
  align-items: center;
  gap: var(---fds-global-space-xs);
  width: 100%;
  min-width: 0;
}

.UIMenuDropdown__icon {
  flex-shrink: 0;
  --size: 1.125rem;
  --color: var(--icon-secondary, #747481);
}

// Box the spinner to the icon's footprint (UISpinner sm is 1rem) so swapping one for
// the other leaves the label in place.
.UIMenuDropdown__spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.125rem;
  height: 1.125rem;
}

// Wrapper the row's content gets when it carries a tooltip — Tippy's trigger <span>, which
// is inline by default. Blocked out and stretched so the whole visible row is the hover
// target rather than just the text.
.UIMenuDropdown__row {
  display: block;
  width: 100%;
  min-width: 0;
}

// Destructive action (e.g. Delete): red text + icon.
.UIMenuDropdown__item[data-variant='destructive'] {
  color: var(--text-status-error, #850305);

  .UIMenuDropdown__icon {
    --color: var(--icon-status-error, #850305);
  }
}

// Thin rule between groups. It has to sit on ui-lib's <li> so it spans the row's full
// padded width; the row itself only knows that it starts a group.
.UIDropdownListItem:has(.UIMenuDropdown__item[data-group-start='true']) {
  margin-top: var(---fds-global-space-xxs);
  border-top: 1px solid $color-neutral-10;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.UIDropdownListItem:has(.UIMenuDropdown__item) {
  // Disabled rows are dimmed by ui-lib and their click is already guarded, but they must
  // stay hoverable so the reason tooltip can open — so drop the hover highlight instead
  // of `pointer-events: none`, and the row still reads as inert.
  &[data-disabled='true'] {
    opacity: 0.5;

    &:hover {
      background-color: transparent;
    }
  }
}
</style>
